import { useRef, useState, type PointerEvent, type ReactNode } from 'react';

/**
 * SwipeableRow — wraps a list row with iOS-style swipe-reveal actions.
 *
 * Swipe RIGHT (gesture starts at left edge, content moves right) reveals
 * actions on the LEFT side. Swipe LEFT reveals actions on the RIGHT side.
 *
 * The component:
 *   - Locks into "swipe mode" only after 8px horizontal movement, so
 *     short taps still propagate as clicks to the wrapped row.
 *   - Uses `touch-action: pan-y` so vertical scroll is uninterrupted.
 *   - Snaps open/closed at 50% of the action width threshold.
 *   - Auto-closes (with a brief delay so the snap reads) when an action
 *     button is tapped.
 *
 * Used by ConversationRow to expose Mark unread/read + Call + Archive.
 */

export interface SwipeAction {
  /** Short label shown beneath the icon. */
  label: string;
  /** Inline SVG/icon (h-5 w-5 sized). */
  icon: ReactNode;
  /** Tailwind background + text color class string for the action tile. */
  bgClass: string;
  /** Fired when the user taps this action button. */
  onAction: () => void;
}

interface SwipeableRowProps {
  /** Actions revealed by swipe-RIGHT (rendered on the left side). */
  leftActions?: SwipeAction[];
  /** Actions revealed by swipe-LEFT (rendered on the right side). */
  rightActions?: SwipeAction[];
  /** The row content (typically the existing ConversationRow markup). */
  children: ReactNode;
}

const ACTION_WIDTH = 76; // px per action tile
const INTENT_PX = 8; // horizontal movement before locking into swipe mode
const SNAP_THRESHOLD = 0.5; // open if dragged past this fraction of the bar

export function SwipeableRow({
  leftActions = [],
  rightActions = [],
  children,
}: SwipeableRowProps) {
  const [offsetX, setOffsetX] = useState(0);
  const [animating, setAnimating] = useState(true);
  const startX = useRef(0);
  const startOffset = useRef(0);
  const dragging = useRef(false);
  const horizontalLock = useRef(false);

  const leftWidth = leftActions.length * ACTION_WIDTH;
  const rightWidth = rightActions.length * ACTION_WIDTH;

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    // Ignore right/middle mouse clicks
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    startX.current = e.clientX;
    startOffset.current = offsetX;
    dragging.current = true;
    horizontalLock.current = false;
    setAnimating(false);
    // 2026-05-26 fix: do NOT setPointerCapture here. When the user just
    // CLICKS the row (no drag), capturing the pointer on pointerdown causes
    // the browser to redirect the resulting click event to THIS wrapping
    // <div> instead of the inner <button>, which has onClick={handleActivate}.
    // Result on desktop: clicks "do nothing" because the button's onClick
    // never fires. Touch devices were less affected due to event-timing
    // differences, which is why the bug looked desktop-only.
    // We now capture lazily inside handlePointerMove the first time the
    // user crosses INTENT_PX of horizontal movement — that's when we
    // actually need the pointer to stay glued to this element through a
    // drag, even if the cursor leaves the row.
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const dx = e.clientX - startX.current;
    if (!horizontalLock.current) {
      if (Math.abs(dx) < INTENT_PX) return;
      horizontalLock.current = true;
      // Drag intent confirmed — NOW capture the pointer so further moves
      // route here even if the cursor exits the row.
      try {
        (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
      } catch {
        /* ignore — some platforms throw if already captured */
      }
    }
    let next = startOffset.current + dx;
    next = Math.max(-rightWidth, Math.min(leftWidth, next));
    setOffsetX(next);
  };

  const handlePointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setAnimating(true);
    const wasHorizontal = horizontalLock.current;
    horizontalLock.current = false;
    if (!wasHorizontal) {
      // Was a tap, not a swipe — leave offset alone (it's already the
      // pre-drag value because we never moved it).
      return;
    }
    if (offsetX > leftWidth * SNAP_THRESHOLD && leftWidth > 0) {
      setOffsetX(leftWidth);
    } else if (offsetX < -rightWidth * SNAP_THRESHOLD && rightWidth > 0) {
      setOffsetX(-rightWidth);
    } else {
      setOffsetX(0);
    }
  };

  const closeAndAct = (action: SwipeAction) => {
    setAnimating(true);
    setOffsetX(0);
    // Defer briefly so the user sees the close animation before the action
    // mutates state (e.g. removes the row from the list).
    window.setTimeout(() => action.onAction(), 180);
  };

  // When the row is open, intercept clicks on the underlying content so a
  // tap closes the swipe instead of triggering the row's onClick.
  const isOpen = offsetX !== 0;
  const handleContentClickCapture = (e: React.MouseEvent) => {
    if (!isOpen) return;
    e.stopPropagation();
    e.preventDefault();
    setAnimating(true);
    setOffsetX(0);
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{ touchAction: 'pan-y' }}
    >
      {/* Left action bar (revealed by swipe-RIGHT). Buttons are positioned
          with negative offset so they appear to slide IN from beneath the
          row as the content slides right. */}
      {leftActions.length > 0 && (
        <div
          className="absolute inset-y-0 left-0 flex"
          aria-hidden={offsetX <= 0}
        >
          {leftActions.map((a, i) => (
            <button
              key={i}
              type="button"
              onClick={() => closeAndAct(a)}
              tabIndex={offsetX <= 0 ? -1 : 0}
              className={`flex flex-col items-center justify-center gap-1 ${a.bgClass}`}
              style={{ width: ACTION_WIDTH }}
              aria-label={a.label}
            >
              {a.icon}
              <span className="text-[11px] font-semibold leading-none">{a.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Right action bar (revealed by swipe-LEFT). */}
      {rightActions.length > 0 && (
        <div
          className="absolute inset-y-0 right-0 flex"
          aria-hidden={offsetX >= 0}
        >
          {rightActions.map((a, i) => (
            <button
              key={i}
              type="button"
              onClick={() => closeAndAct(a)}
              tabIndex={offsetX >= 0 ? -1 : 0}
              className={`flex flex-col items-center justify-center gap-1 ${a.bgClass}`}
              style={{ width: ACTION_WIDTH }}
              aria-label={a.label}
            >
              {a.icon}
              <span className="text-[11px] font-semibold leading-none">{a.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Sliding content layer */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClickCapture={handleContentClickCapture}
        style={{
          transform: `translateX(${offsetX}px)`,
          transition: animating
            ? 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1)'
            : 'none',
        }}
        className="relative bg-white dark:bg-[#0F172A]"
      >
        {children}
      </div>
    </div>
  );
}
